import React, { memo } from 'react';

import { MessageList } from '@components/MessageList';
import { isLocalOrTestEnv } from '@utils/featureFlags';
import { ResolveMessageForm } from '@views/MessagesPage/MessageForms/ResolveMessageForm';

import { IWidget, ParsedEntry } from '../types';

import { AcuteContactInformation } from './Widgets/AcuteContactInformation';
import { ApartmentRecommendations } from './Widgets/ApartmentRecommendations';
import { ApartmentSearchWatchBanner } from './Widgets/ApartmentSearchWatchBanner';
import { CustomerBenefit } from './Widgets/CustomerBenefit';
import { ParkingSpaceSwitchWidget } from './Widgets/ParkingSpaceSwitchWidget';
import { ParkingSpaceTerminationWidget } from './Widgets/ParkingSpaceTerminationWidget';
import { ParkingSpaceWatchWidget } from './Widgets/ParkingSpaceWatchWidget';
import { SaunaBooking } from './Widgets/SaunaBooking';
import { SaunaReservationServiceParty } from './Widgets/SaunaReservationServiceParty';
import { SaunaReservationSystem } from './Widgets/SaunaReservationSystem';
import { ServiceParty } from './Widgets/ServiceParty';
import { WaterUsage } from './Widgets/WaterUsage';
import {
  ApartmentTemperature,
  HomeChangerSwitchButton,
  MyHomeHeader,
} from './Widgets';

interface Props {
  data: ParsedEntry<IWidget>;
}

export const GenericWidget = memo(({ data }: Props) => {
  switch (data.component) {
    case 'myHomeHeader':
      return <MyHomeHeader {...data} />;
    case 'messages':
      return <MessageList limit={4} />;
    case 'servicePartyInfo':
      return <ServiceParty data={data} />;
    case 'saunaBooking':
      return <SaunaBooking data={data} />;
    case 'acuteContactInformation':
      return <AcuteContactInformation data={data} />;
    case 'form':
      return <ResolveMessageForm data={data} />;
    case 'apartmentTemperature':
      return <ApartmentTemperature />;
    case 'apartmentWaterUsage':
      return <WaterUsage />;
    case 'customerBenefit':
      return <CustomerBenefit {...data} />;
    case 'apartmentSearchWatchBanner':
      return <ApartmentSearchWatchBanner />;
    case 'apartmentRecommendationsNear':
      return <ApartmentRecommendations recommendationsDistance="near" />;
    case 'apartmentRecommendationsFar':
      return <ApartmentRecommendations recommendationsDistance="far" />;
    case 'saunaReservationServiceParty':
      return <SaunaReservationServiceParty />;
    case 'saunaReservationSystem':
      return <SaunaReservationSystem translations={data.customTranslations} />;
    case 'parkingSpaceWatchButton':
      return <ParkingSpaceWatchWidget {...data} />;
    case 'parkingSpaceSwitchButton':
      return <ParkingSpaceSwitchWidget {...data} />;
    case 'parkingSpaceTerminationButton':
      return <ParkingSpaceTerminationWidget {...data} />;
    case 'homeChangerSwitchButton':
      return <HomeChangerSwitchButton {...data} />;
    default:
      if (isLocalOrTestEnv) {
        console.warn('Unresolved Contentful widget type:', data.component);
      }

      return null;
  }
});
